
import { useState } from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Webhook, KeyRound, RefreshCw, Plus, Trash2, LinkIcon } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function Developer() {
  const [activeTab, setActiveTab] = useState('api-keys');
  const [apiKeys, setApiKeys] = useState([
    { id: 1, name: 'Production Key', key: 'drimt_live_12345...', created: '2023-10-15', lastUsed: '2023-11-30', active: true },
    { id: 2, name: 'Development Key', key: 'drimt_test_67890...', created: '2023-11-05', lastUsed: '2023-11-29', active: true },
  ]);
  
  const [webhooks, setWebhooks] = useState([
    { id: 1, url: 'https://example.com/webhook', events: ['team.created', 'team.updated'], active: true, created: '2023-10-10' },
    { id: 2, url: 'https://api.myapp.com/drimt-events', events: ['project.created', 'task.completed'], active: false, created: '2023-11-15' },
  ]);
  
  const [isCreateKeyDialogOpen, setIsCreateKeyDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  
  const [isCreateWebhookDialogOpen, setIsCreateWebhookDialogOpen] = useState(false);
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  
  const handleCreateApiKey = () => {
    const newKey = {
      id: apiKeys.length + 1,
      name: newKeyName,
      key: `drimt_${Math.random().toString(36).substring(2, 15)}...`,
      created: new Date().toISOString().split('T')[0],
      lastUsed: '-',
      active: true
    };
    
    setApiKeys([...apiKeys, newKey]);
    setNewKeyName('');
    setIsCreateKeyDialogOpen(false);
    toast.success(`API key "${newKeyName}" created successfully`);
  };
  
  const handleCreateWebhook = () => {
    if (!newWebhookUrl || selectedEvents.length === 0) {
      toast.error('Please provide a URL and select at least one event');
      return;
    }
    
    const newWebhook = {
      id: webhooks.length + 1,
      url: newWebhookUrl,
      events: selectedEvents,
      active: true,
      created: new Date().toISOString().split('T')[0]
    };
    
    setWebhooks([...webhooks, newWebhook]);
    setNewWebhookUrl('');
    setSelectedEvents([]);
    setIsCreateWebhookDialogOpen(false);
    toast.success('Webhook endpoint created successfully');
  };
  
  const toggleApiKeyStatus = (id: number) => {
    setApiKeys(apiKeys.map(key => 
      key.id === id ? { ...key, active: !key.active } : key
    ));
    
    const key = apiKeys.find(key => key.id === id);
    if (key) {
      toast.success(`API key "${key.name}" ${key.active ? 'disabled' : 'enabled'}`);
    }
  };
  
  const toggleWebhookStatus = (id: number) => {
    setWebhooks(webhooks.map(hook => 
      hook.id === id ? { ...hook, active: !hook.active } : hook
    ));
    
    const webhook = webhooks.find(hook => hook.id === id);
    if (webhook) {
      toast.success(`Webhook to ${webhook.url} ${webhook.active ? 'disabled' : 'enabled'}`);
    }
  };
  
  const deleteApiKey = (id: number) => {
    const key = apiKeys.find(key => key.id === id);
    setApiKeys(apiKeys.filter(key => key.id !== id));
    if (key) {
      toast.success(`API key "${key.name}" deleted`);
    }
  };
  
  const deleteWebhook = (id: number) => {
    const webhook = webhooks.find(hook => hook.id === id);
    setWebhooks(webhooks.filter(hook => hook.id !== id));
    if (webhook) {
      toast.success(`Webhook to ${webhook.url} deleted`);
    }
  };
  
  const copyApiKey = (key: string) => {
    navigator.clipboard.writeText(key.replace('...', '******'));
    toast.success('API key copied to clipboard');
  };
  
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Developer Settings</h2>
            <p className="text-muted-foreground">
              Manage API keys, webhooks, and developer tools.
            </p>
          </div>
          <Webhook className="h-6 w-6 text-muted-foreground" />
        </div>
        <Separator />
        
        <div className="flex gap-8">
          <div className="w-1/5">
            <nav className="flex flex-col space-y-2">
              <NavLink to="/settings" className="p-2 rounded-md hover:bg-accent">General</NavLink>
              <NavLink to="/settings/developer" className="p-2 rounded-md hover:bg-accent">Developer</NavLink>
            </nav>
          </div>
          
          <div className="flex-1">
            <Tabs defaultValue="api-keys" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="api-keys">API Keys</TabsTrigger>
                <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
              </TabsList>
              
              <TabsContent value="api-keys" className="mt-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>API Keys</CardTitle>
                        <CardDescription>
                          Create and manage API keys for accessing Drimt's API.
                        </CardDescription>
                      </div>
                      <KeyRound className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="rounded-md border">
                        <table className="w-full caption-bottom text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50 transition-colors">
                              <th className="h-12 px-4 text-left align-middle font-medium">Name</th>
                              <th className="h-12 px-4 text-left align-middle font-medium">Key</th>
                              <th className="h-12 px-4 text-left align-middle font-medium">Created</th>
                              <th className="h-12 px-4 text-left align-middle font-medium">Last Used</th>
                              <th className="h-12 px-4 text-left align-middle font-medium">Status</th>
                              <th className="h-12 px-4 text-right align-middle font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {apiKeys.map((apiKey) => (
                              <tr key={apiKey.id} className="border-b transition-colors hover:bg-muted/50">
                                <td className="p-4 align-middle">{apiKey.name}</td>
                                <td className="p-4 align-middle font-mono text-xs">
                                  <div className="flex items-center space-x-2">
                                    <span>{apiKey.key}</span>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-8 w-8 p-0"
                                      onClick={() => copyApiKey(apiKey.key)}
                                    >
                                      <span className="sr-only">Copy</span>
                                      <LinkIcon className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                                <td className="p-4 align-middle">{apiKey.created}</td>
                                <td className="p-4 align-middle">{apiKey.lastUsed}</td>
                                <td className="p-4 align-middle">
                                  <Badge variant={apiKey.active ? "default" : "secondary"}>
                                    {apiKey.active ? "Active" : "Inactive"}
                                  </Badge>
                                </td>
                                <td className="p-4 align-middle text-right">
                                  <div className="flex justify-end space-x-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => toggleApiKeyStatus(apiKey.id)}
                                    >
                                      {apiKey.active ? "Disable" : "Enable"}
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => deleteApiKey(apiKey.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Dialog open={isCreateKeyDialogOpen} onOpenChange={setIsCreateKeyDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full">
                          <Plus className="mr-2 h-4 w-4" />
                          Create New API Key
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Create API Key</DialogTitle>
                          <DialogDescription>
                            Give your API key a name to remember what it's used for.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="name" className="text-right">
                              Key Name
                            </Label>
                            <Input
                              id="name"
                              placeholder="e.g. Production Backend"
                              className="col-span-3"
                              value={newKeyName}
                              onChange={(e) => setNewKeyName(e.target.value)}
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button type="submit" onClick={handleCreateApiKey} disabled={!newKeyName}>
                            Create Key
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </CardFooter>
                </Card>
              </TabsContent>
              
              <TabsContent value="webhooks" className="mt-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Webhooks</CardTitle>
                        <CardDescription>
                          Manage webhook endpoints to receive real-time event notifications.
                        </CardDescription>
                      </div>
                      <Webhook className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="rounded-md border">
                        <table className="w-full caption-bottom text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50 transition-colors">
                              <th className="h-12 px-4 text-left align-middle font-medium">Endpoint URL</th>
                              <th className="h-12 px-4 text-left align-middle font-medium">Events</th>
                              <th className="h-12 px-4 text-left align-middle font-medium">Created</th>
                              <th className="h-12 px-4 text-left align-middle font-medium">Status</th>
                              <th className="h-12 px-4 text-right align-middle font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {webhooks.map((webhook) => (
                              <tr key={webhook.id} className="border-b transition-colors hover:bg-muted/50">
                                <td className="p-4 align-middle font-mono text-xs">{webhook.url}</td>
                                <td className="p-4 align-middle">
                                  <div className="flex flex-wrap gap-1">
                                    {webhook.events.map((event) => (
                                      <Badge key={event} variant="outline" className="text-xs">
                                        {event}
                                      </Badge>
                                    ))}
                                  </div>
                                </td>
                                <td className="p-4 align-middle">{webhook.created}</td>
                                <td className="p-4 align-middle">
                                  <Badge variant={webhook.active ? "default" : "secondary"}>
                                    {webhook.active ? "Active" : "Inactive"}
                                  </Badge>
                                </td>
                                <td className="p-4 align-middle text-right">
                                  <div className="flex justify-end space-x-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => toggleWebhookStatus(webhook.id)}
                                    >
                                      {webhook.active ? "Disable" : "Enable"}
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => deleteWebhook(webhook.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Dialog open={isCreateWebhookDialogOpen} onOpenChange={setIsCreateWebhookDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full">
                          <Plus className="mr-2 h-4 w-4" />
                          Add Webhook Endpoint
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Create Webhook</DialogTitle>
                          <DialogDescription>
                            Add a new webhook endpoint to receive event notifications.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="webhook-url" className="text-right">
                              Endpoint URL
                            </Label>
                            <Input
                              id="webhook-url"
                              placeholder="https://example.com/webhook"
                              className="col-span-3"
                              value={newWebhookUrl}
                              onChange={(e) => setNewWebhookUrl(e.target.value)}
                            />
                          </div>
                          <div className="grid grid-cols-4 items-start gap-4">
                            <Label htmlFor="events" className="text-right pt-2">
                              Events
                            </Label>
                            <div className="col-span-3 space-y-2">
                              {['team.created', 'team.updated', 'team.deleted', 'project.created', 'project.updated', 'task.completed'].map((event) => (
                                <div key={event} className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    id={`event-${event}`}
                                    checked={selectedEvents.includes(event)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedEvents([...selectedEvents, event]);
                                      } else {
                                        setSelectedEvents(selectedEvents.filter(e => e !== event));
                                      }
                                    }}
                                    className="rounded border-gray-300"
                                  />
                                  <label htmlFor={`event-${event}`} className="text-sm">
                                    {event}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button 
                            type="submit" 
                            onClick={handleCreateWebhook}
                            disabled={!newWebhookUrl || selectedEvents.length === 0}
                          >
                            Create Webhook
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </CardFooter>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
